import { useParams } from 'react-router-dom';

import {
  Collapsible,
  InfoRow,
} from '@/components/documents-v2/components/inspector/InspectorComponents';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import {
  ArtifactDigest,
  ArtifactType,
} from '@/services/types/artifact-digest-types';

interface DigestOverviewProps {
  digest: ArtifactDigest | null | undefined;
  artifactId: string;
}

export const DigestOverview = ({ digest, artifactId }: DigestOverviewProps) => {
  const { matter_id: matterIdFromParams } = useParams<{ matter_id: string }>();
  const matterId = matterIdFromParams || '';

  const { data: artifactData } = useGetArtifactUnpackedQuery(
    {
      matterId,
      artifactId,
    },
    {
      skip: !artifactId || !matterId,
    },
  );

  if (!digest) {
    return (
      <div className="text-sm text-muted-foreground p-2">
        No details available
      </div>
    );
  }

  // Helpers ----------------------------------------------------
  const artifactType = artifactData?.artifact_type;
  const isBlob = artifactType === ArtifactType.BLOB;
  const isAudioConversation = artifactType === ArtifactType.TRANSCRIPT;

  // Collapsible debug section toggles are handled by Collapsible component

  // Determine the primary summary text to display
  const primarySummary =
    // digest.brief ||
    digest.one_line_summary ||
    digest.three_line_summary ||
    digest.long_form_description;

  return (
    <div className="space-y-4 py-2 justify-between  flex-col flex h-full">
      {/* ----------------- TITLE & SUMMARY ----------------- */}
      <div className="flex flex-col justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-base font-semibold leading-snug">
            {digest.title || 'Untitled'}
          </h3>

          {primarySummary && (
            <p className="text-sm  leading-snug pt-1 whitespace-pre-wrap">
              {primarySummary}
            </p>
          )}
        </div>
      </div>
      <div>
        {/* ----------------- COLLAPSIBLE DEBUG ----------------- */}
        <div className="opacity-50">
          <div className="space-y-1">
            <InfoRow label="Digest ID" value={digest.id} />
            <InfoRow label="Artifact ID" value={artifactId} />
            {matterId && <InfoRow label="Matter ID" value={matterId} />}
            {isBlob && artifactData?.blob?.id && (
              <InfoRow label="Blob ID" value={artifactData.blob.id} />
            )}
            {isAudioConversation &&
              artifactData?.transcript?.conversation?.id && (
                <>
                  <InfoRow
                    label="Transcript ID"
                    value={artifactData.transcript.id}
                  />
                  <InfoRow
                    label="Conversation ID"
                    value={artifactData.transcript.conversation.id}
                  />
                </>
              )}
          </div>
          <Collapsible title="Raw JSON">
            <pre className="text-xs whitespace-pre-wrap break-all max-h-64 overflow-auto border border-zinc-200 dark:border-zinc-800 rounded-md p-2 bg-zinc-50 dark:bg-zinc-900">
              {JSON.stringify({ digest, artifact: artifactData }, null, 2)}
            </pre>
          </Collapsible>
        </div>
        {/* ----------------- RAW JSON VIEWER ----------------- */}
      </div>
    </div>
  );
};
